import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useRouteConfiguration } from '../../../../context/routeConfigurationContext.js';
import { matchPathname } from '../../../../util/routes.js';

import { NamedLink, ExternalLink } from '../../../../components/index.js';
import css from './Link.module.css';

/**
 * Link element which internally uses NamedLink or ExternalLink
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {ReactNode} props.children
 * @param {string} props.href link destination. In-app links need to start with '/'.
 * @returns {JSX.Element} link element
 */
export const Link = React.forwardRef((props, ref) => {
  const location = useLocation();
  const currentUser = useSelector(state => state.user?.currentUser);
  const routes = useRouteConfiguration();

  const { className, rootClassName, href, title, children } = props;
  const shouldRedirectToSignup =
    !currentUser && typeof href === 'string' && href.startsWith('/l/new');
  const resolvedHref = shouldRedirectToSignup ? '/signup?userType=customer' : href;
  const classes = classNames(rootClassName || css.link, className);
  const titleMaybe = title ? { title } : {};
  const linkProps = { className: classes, href: resolvedHref, children, ...titleMaybe };

  // Markdown parser (rehype-sanitize) might return undefined href
  if (!resolvedHref || !children) {
    return null;
  }

  if (resolvedHref.charAt(0) === '/') {
    // Internal link
    const testURL = new URL('http://my.marketplace.com' + resolvedHref);
    const matchedRoutes = matchPathname(testURL.pathname, routes);
    if (matchedRoutes.length > 0) {
      const found = matchedRoutes[0];
      const to = { search: testURL.search, hash: testURL.hash };
      return (
        <NamedLink name={found.route.name} params={found.params} to={to} {...linkProps} ref={ref} />
      );
    }
  }

  if (resolvedHref.charAt(0) === '#') {
    // Resolved from the router location, which the server has as well: a window-only branch
    // rendered an ExternalLink on the server and a NamedLink on the client (backlog #4).
    const hash = resolvedHref;
    let testURL = new URL(
      `http://my.marketplace.com${location.pathname}${location.hash}${location.search}`
    );
    testURL.hash = hash;
    const matchedRoutes = matchPathname(testURL.pathname, routes);
    if (matchedRoutes.length > 0) {
      const found = matchedRoutes[0];
      const to = { search: testURL.search, hash: testURL.hash };
      return (
        <NamedLink name={found.route.name} params={found.params} to={to} {...linkProps} ref={ref} />
      );
    }
  }

  return <ExternalLink {...linkProps} ref={ref} />;
});

Link.displayName = 'Link';
